import { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useStore } from "../lib/store";
import { Icon } from "./Icons";

/* ⌘K / Ctrl-K command palette — jump to any project, sheet, invoice, RFI or
   review across the whole studio. Mounted once in the Shell. */
interface Hit { id: string; title: string; sub: string; kind: string; to: string }

export function CommandPalette() {
  const { projects } = useStore();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
      else if (e.key === "Escape") setOpen(false);
    };
    const openEv = () => setOpen(true);
    window.addEventListener("keydown", f);
    window.addEventListener("cmdk-open", openEv);
    return () => { window.removeEventListener("keydown", f); window.removeEventListener("cmdk-open", openEv); };
  }, []);
  useEffect(() => { if (open) { setQ(""); setSel(0); setTimeout(() => inputRef.current?.focus(), 30); } }, [open]);

  const hits = useMemo<Hit[]>(() => {
    const s = q.trim().toLowerCase();
    const out: Hit[] = [];
    const push = (h: Hit) => { if (out.length < 40) out.push(h); };
    for (const p of (projects ?? [])) {
      if (!s || `${p.name} ${p.code} ${p.client?.name ?? ""} ${p.client?.company ?? ""}`.toLowerCase().includes(s)) {
        push({ id: p.id, title: p.name, sub: `${p.code} · ${p.client?.name ?? ""}`, kind: "Project", to: `/studio/${p.id}/dashboard` });
      }
      if (!s) continue;   // with no query, list only projects — not every sub-entity
      for (const d of (p.deliverables ?? [])) if (`${d.code} ${d.title}`.toLowerCase().includes(s)) push({ id: p.id + d.id, title: `${d.code} — ${d.title}`, sub: `${p.name} · drawing`, kind: "Sheet", to: `/studio/${p.id}/drawings` });
      for (const i of (p.invoices ?? [])) if (`${i.ref} ${i.title}`.toLowerCase().includes(s)) push({ id: p.id + i.id, title: `${i.ref} — ${i.title}`, sub: `${p.name} · invoice`, kind: "Invoice", to: `/studio/${p.id}/billing` });
      for (const r of (p.rfis ?? [])) if (`${r.ref} ${r.subject}`.toLowerCase().includes(s)) push({ id: p.id + r.id, title: `${r.ref} — ${r.subject}`, sub: `${p.name} · RFI`, kind: "RFI", to: `/studio/${p.id}/rfis` });
      for (const rv of (p.reviews ?? [])) if ((rv.title || "").toLowerCase().includes(s)) push({ id: p.id + rv.id, title: rv.title, sub: `${p.name} · review`, kind: "Review", to: `/studio/${p.id}/review` });
    }
    return out;
  }, [q, projects]);

  useEffect(() => { setSel(0); }, [q]);
  if (!open) return null;
  const go = (h: Hit) => { setOpen(false); nav(h.to); };

  return createPortal(
    <div onMouseDown={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 120, background: "rgba(0,0,0,.4)", backdropFilter: "blur(3px)", display: "grid", placeItems: "start center", paddingTop: "12vh" }}>
      <div onMouseDown={(e) => e.stopPropagation()} className="card" style={{ width: "min(640px, 92vw)", overflow: "hidden", boxShadow: "var(--shadow-lg)" }}>
        <div className="row" style={{ gap: 10, padding: "13px 16px", borderBottom: "1px solid var(--line)" }}>
          <Icon.brief width={16} style={{ color: "var(--ink-3)", flexShrink: 0 }} />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(hits.length - 1, s + 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
              else if (e.key === "Enter" && hits[sel]) go(hits[sel]);
            }}
            placeholder="Search projects, sheets, invoices, RFIs…" style={{ border: "none", background: "transparent", outline: "none", fontSize: 15, flex: 1, color: "var(--ink)" }} />
          <span className="badge" style={{ fontSize: 9 }}>ESC</span>
        </div>
        <div className="col" style={{ maxHeight: "56vh", overflowY: "auto", padding: 6 }}>
          {hits.length === 0 && <div className="dim" style={{ padding: 22, textAlign: "center", fontSize: 13 }}>{q ? "No matches." : "Type to search across every project."}</div>}
          {hits.map((h, i) => (
            <button key={h.id} onMouseEnter={() => setSel(i)} onClick={() => go(h)} className="row between"
              style={{ padding: "9px 12px", borderRadius: 8, gap: 10, textAlign: "left", border: "none", cursor: "pointer", background: i === sel ? "var(--paper-2)" : "transparent" }}>
              <span className="row" style={{ gap: 10, minWidth: 0 }}>
                <span style={{ fontSize: 13.5, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 380 }}>{h.title}</span>
                <span className="dim" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{h.sub}</span>
              </span>
              <span className="badge" style={{ fontSize: 8.5, flexShrink: 0 }}>{h.kind}</span>
            </button>
          ))}
        </div>
      </div>
    </div>, document.body);
}
